import type { CreateTaskPayload, Task, UpdateTaskPayload } from '../types/Tasks';
import BaseAPI from './BaseAPI';
import type AgentCoreAPI from './AgentCoreAPI';

const TASKS_PATH = '/v2/task-management/tasks';

export default class TasksAPI extends BaseAPI {
  private async getToken(organizationId: string): Promise<string> {
    // Delegate to AgentCoreAPI — same IDP exchange, shared token cache.
    const agentCore: AgentCoreAPI = (this.host as unknown as { api: { agentCore: AgentCoreAPI } })
      .api.agentCore;
    return agentCore.getOAuthToken(organizationId);
  }

  private taskUrl(suffix = ''): string {
    return `${this.host.tcUrl}${TASKS_PATH}${suffix}`;
  }

  async create(organizationId: string, payload: CreateTaskPayload): Promise<Task> {
    const token = await this.getToken(organizationId);
    const res = await fetch(this.taskUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`create task failed: ${res.status}`);
    return res.json();
  }

  async complete(organizationId: string, taskId: string): Promise<Task> {
    const token = await this.getToken(organizationId);
    const res = await fetch(this.taskUrl(`/${taskId}/complete`), {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`complete task failed: ${res.status}`);
    return res.json();
  }

  async reopen(organizationId: string, taskId: string): Promise<Task> {
    const token = await this.getToken(organizationId);
    const res = await fetch(this.taskUrl(`/${taskId}`), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ status: 'open' }),
    });
    if (!res.ok) throw new Error(`reopen task failed: ${res.status}`);
    return res.json();
  }

  async update(organizationId: string, taskId: string, payload: UpdateTaskPayload): Promise<Task> {
    const token = await this.getToken(organizationId);
    const res = await fetch(this.taskUrl(`/${taskId}`), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`update task failed: ${res.status}`);
    return res.json();
  }

  async list(
    organizationId: string,
    filter: 'open' | 'completed' = 'open',
    paging: { limit?: number; cursor?: string } = {}
  ): Promise<{ paging: { next_cursor: string | null }; tasks: Task[] }> {
    const token = await this.getToken(organizationId);
    const statuses = filter === 'open' ? ['open', 'overdue'] : ['completed'];
    const query = new URLSearchParams();
    statuses.forEach((status) => query.append('status', status));
    if (paging.limit !== undefined) query.append('limit', String(paging.limit));
    if (paging.cursor !== undefined) query.append('cursor', paging.cursor);

    const res = await fetch(this.taskUrl(`?${query.toString()}`), {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`list tasks failed: ${res.status}`);
    return res.json();
  }
}
